import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  BOARD_W,
  NODE_COLOR_CLASS,
  TMT_HIT_SCALE,
  type NodePosition,
  type NodeShape,
  type NodeColor,
} from '@/pages/tmt-spick/tmtConfig';
import { playSnapSound, unlockAudio } from '@/pages/tmt-spick/utils/sound';

export interface BoardNode extends NodePosition {
  id: string;
  label?: string;
  shape?: NodeShape;
  color?: NodeColor;
}

interface BoardProps {
  nodes: BoardNode[];
  boardH: number;
  path: string[];
  wrongId: string | null;
  completed: boolean;
  onSelect: (id: string) => void;
  nodeSize?: number;
}

interface Pt {
  x: number;
  y: number;
}

interface Frame {
  left: number;
  top: number;
  width: number;
  height: number;
}

const EMPTY_FRAME: Frame = { left: 0, top: 0, width: 0, height: 0 };

// 도형별 스타일 (Stage 1은 원형, Stage 2/3는 정답 조건의 도형 표시)
function shapeStyle(shape: NodeShape | undefined): CSSProperties {
  if (shape === 'square') return { borderRadius: '10px' };
  if (shape === 'triangle') {
    return { borderRadius: 0, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' };
  }
  return { borderRadius: '9999px' };
}

// 순서 잇기 노드 보드 — 노드 간 드래그 연결 방식으로 진행한다.
//
// 정렬 원칙 (핵심):
// - 보드 영역 안에 viewBox 비율(BOARD_W : boardH)과 정확히 같은 프레임을 계산한다.
// - 선(SVG)과 노드(div) 모두 "같은 프레임 + 같은 픽셀 배율"로만 그린다.
// - 노드는 translate(-50%,-50%) 로 중심을 맞추지 않는다. left/top 에서 노드 크기의
//   절반을 직접 빼서 중심을 맞춘다. (transform 은 scale 애니메이션 전용)
//   → 예전에는 활성 노드의 pulse 애니메이션이 transform 을 덮어써서 노드가 반 칸씩
//     밀려나고, 선이 노드 중심을 벗어나 보였다. 색/도형이 있는 2·3단계에서만 두드러졌다.
export default function Board({
  nodes,
  boardH,
  path,
  wrongId,
  completed,
  onSelect,
  nodeSize = 56,
}: BoardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<Frame>(EMPTY_FRAME);
  const [dragging, setDragging] = useState(false);
  const [dragEnd, setDragEnd] = useState<Pt | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [emptyWrong, setEmptyWrong] = useState(false);

  // 보드 영역 안에서 viewBox 비율을 유지하는 프레임 크기 계산
  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const scale = Math.min(rect.width / BOARD_W, rect.height / boardH);
    const width = BOARD_W * scale;
    const height = boardH * scale;
    setFrame((prev) => {
      const next = {
        left: (rect.width - width) / 2,
        top: (rect.height - height) / 2,
        width,
        height,
      };
      if (
        Math.abs(prev.left - next.left) < 0.5 &&
        Math.abs(prev.top - next.top) < 0.5 &&
        Math.abs(prev.width - next.width) < 0.5 &&
        Math.abs(prev.height - next.height) < 0.5
      ) {
        return prev;
      }
      return next;
    });
  }, [boardH]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // 화면 회전 / 리사이즈 / 주소창 변화에도 프레임을 계속 동기화
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [measure]);

  const scale = frame.width > 0 ? frame.width / BOARD_W : 0;
  const ready = scale > 0;

  // 선은 도형 위에 그려진다. 선 끝이 도형 안쪽으로 파고들지 않도록
  // 각 도형의 실제 경계까지의 거리를 보드 좌표로 환산해 정확히 잘라낸다.
  const pxPerUnit = scale > 0 ? scale : 1;
  const halfBoard = nodeSize / 2 / pxPerUnit;
  const strokeBoard = Math.max(2.5, 5 * scale) / pxPerUnit;
  const padBoard = strokeBoard / 2 + 2 / pxPerUnit;

  // 중심에서 해당 방향으로 도형 경계까지의 거리 (보드 좌표)
  const edgeDistance = (
    shape: NodeShape | undefined,
    ux: number,
    uy: number,
  ): number => {
    const half = halfBoard;
    if (shape === 'square') {
      return half / Math.max(Math.abs(ux), Math.abs(uy), 0.001);
    }
    if (shape === 'triangle') {
      const pts: Pt[] = [
        { x: 0, y: -half },
        { x: -half, y: half },
        { x: half, y: half },
      ];
      let best = Number.POSITIVE_INFINITY;
      for (let i = 0; i < 3; i += 1) {
        const a = pts[i];
        const b = pts[(i + 1) % 3];
        const ex = b.x - a.x;
        const ey = b.y - a.y;
        const den = ux * ey - uy * ex;
        if (Math.abs(den) < 1e-6) continue;
        const t = (a.x * ey - a.y * ex) / den;
        if (t <= 0) continue;
        const sx = ux * t;
        const sy = uy * t;
        const s = ((sx - a.x) * ex + (sy - a.y) * ey) / (ex * ex + ey * ey);
        if (s >= -0.001 && s <= 1.001 && t < best) best = t;
      }
      return Number.isFinite(best) ? best : half;
    }
    return half;
  };

  const lines = path
    .slice(0, -1)
    .map((id, i) => {
      const from = nodes.find((n) => n.id === id);
      const to = nodes.find((n) => n.id === path[i + 1]);
      if (!from || !to) return null;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const dist = Math.hypot(dx, dy) || 1;
      const ux = dx / dist;
      const uy = dy / dist;
      const startTrim = Math.min(edgeDistance(from.shape, ux, uy) + padBoard, dist * 0.45);
      const endTrim = Math.min(edgeDistance(to.shape, -ux, -uy) + padBoard, dist * 0.45);
      return {
        x1: from.x + ux * startTrim,
        y1: from.y + uy * startTrim,
        x2: to.x - ux * endTrim,
        y2: to.y - uy * endTrim,
        key: `${id}-${path[i + 1]}`,
      };
    })
    .filter(
      (l): l is { x1: number; y1: number; x2: number; y2: number; key: string } =>
        l !== null,
    );

  const selectedSet = new Set(path);
  const activeId = path.length > 0 ? path[path.length - 1] : null;
  const activeNode = nodes.find((n) => n.id === activeId) ?? null;
  const hoverNode = nodes.find((n) => n.id === hoverId) ?? null;

  // 컨테이너 로컬 픽셀 → 보드(SVG viewBox) 좌표
  const toBoard = (px: number, py: number): Pt => {
    if (scale === 0) return { x: 0, y: 0 };
    return {
      x: (px - frame.left) / scale,
      y: (py - frame.top) / scale,
    };
  };

  const getLocal = (clientX: number, clientY: number): Pt => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  // 확대된 터치 판정 영역으로 손가락 아래 노드 탐색
  const nodeAt = (px: number, py: number): string | null => {
    if (scale === 0) return null;
    const radius = (nodeSize / 2) * TMT_HIT_SCALE;
    let best: string | null = null;
    let bestDist = Infinity;
    for (const n of nodes) {
      const cx = frame.left + n.x * scale;
      const cy = frame.top + n.y * scale;
      const d = Math.hypot(px - cx, py - cy);
      if (d <= radius && d < bestDist) {
        best = n.id;
        bestDist = d;
      }
    }
    return best;
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (completed) return;
    unlockAudio();
    const p = getLocal(e.clientX, e.clientY);
    const id = nodeAt(p.x, p.y);

    if (path.length === 0) {
      if (id) {
        onSelect(id);
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragging(true);
        setDragEnd(toBoard(p.x, p.y));
        setHoverId(null);
      }
      return;
    }

    if (id === activeId) {
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
      setDragEnd(toBoard(p.x, p.y));
      setHoverId(null);
    }
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const p = getLocal(e.clientX, e.clientY);
    setDragEnd(toBoard(p.x, p.y));
    const id = nodeAt(p.x, p.y);
    const nextHover = id && id !== activeId ? id : null;
    if (nextHover && nextHover !== hoverId) {
      playSnapSound();
    }
    setHoverId(nextHover);
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) {
      setDragging(false);
      setDragEnd(null);
      setHoverId(null);
      return;
    }
    const p = getLocal(e.clientX, e.clientY);
    const id = nodeAt(p.x, p.y);

    setDragging(false);
    setDragEnd(null);
    setHoverId(null);

    if (id && id !== activeId) {
      onSelect(id);
    } else {
      setEmptyWrong(true);
      window.setTimeout(() => setEmptyWrong(false), 350);
    }
  };

  const handlePointerCancel = () => {
    setDragging(false);
    setDragEnd(null);
    setHoverId(null);
  };

  const tempLineEnd: Pt | null =
    dragging && activeNode
      ? hoverNode
        ? { x: hoverNode.x, y: hoverNode.y }
        : dragEnd
      : null;

  // 임시선도 도형 중심이 아니라 도형 경계에서 시작/종료한다.
  let dragStart: Pt | null = null;
  let dragTarget: Pt | null = null;
  if (dragging && activeNode && tempLineEnd) {
    const dx = tempLineEnd.x - activeNode.x;
    const dy = tempLineEnd.y - activeNode.y;
    const dist = Math.hypot(dx, dy) || 1;
    const ux = dx / dist;
    const uy = dy / dist;
    const startTrim = Math.min(edgeDistance(activeNode.shape, ux, uy) + padBoard, dist * 0.9);
    dragStart = {
      x: activeNode.x + ux * startTrim,
      y: activeNode.y + uy * startTrim,
    };
    if (hoverNode) {
      const endTrim = Math.min(
        edgeDistance(hoverNode.shape, -ux, -uy) + padBoard,
        dist * 0.45,
      );
      dragTarget = {
        x: tempLineEnd.x - ux * endTrim,
        y: tempLineEnd.y - uy * endTrim,
      };
    } else {
      dragTarget = tempLineEnd;
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full select-none"
      style={{ touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {/* viewBox 비율을 유지하는 프레임: 선과 노드가 같은 좌표계를 공유한다 */}
      <div
        className="absolute"
        style={{
          left: frame.left,
          top: frame.top,
          width: frame.width,
          height: frame.height,
          opacity: ready ? 1 : 0,
        }}
      >
        <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible z-20">
          {lines.map((l) => (
            <g key={l.key}>
              {/* 도형 위에서도 선이 또렷하게 보이도록 얇은 밝은 테두리를 먼저 깐다 */}
              <line
                x1={l.x1 * scale}
                y1={l.y1 * scale}
                x2={l.x2 * scale}
                y2={l.y2 * scale}
                pathLength={1}
                stroke="currentColor"
                strokeWidth={Math.max(2.5, 5 * scale) + 4}
                strokeLinecap="round"
                className="animate-draw-line text-background-50"
              />
              <line
                x1={l.x1 * scale}
                y1={l.y1 * scale}
                x2={l.x2 * scale}
                y2={l.y2 * scale}
                pathLength={1}
                stroke="currentColor"
                strokeWidth={Math.max(2.5, 5 * scale)}
                strokeLinecap="round"
                className={`animate-draw-line ${
                  completed ? 'text-primary-500' : 'text-primary-400'
                }`}
              />
            </g>
          ))}

          {dragStart && dragTarget && (
            <line
              x1={dragStart.x * scale}
              y1={dragStart.y * scale}
              x2={dragTarget.x * scale}
              y2={dragTarget.y * scale}
              stroke="currentColor"
              strokeWidth={Math.max(2.5, 5 * scale)}
              strokeLinecap="round"
              strokeDasharray="10 8"
              className="text-primary-400"
            />
          )}
        </svg>

        {nodes.map((n) => {
          const label = n.label ?? n.id;
          const isSelected = selectedSet.has(n.id);
          const isActive = n.id === activeId;
          const isWrong = n.id === wrongId;
          const isEmptyWrong = emptyWrong && n.id === activeId;
          const isHover = n.id === hoverId;
          const hasColor = !!n.color;

          let cls: string;
          if (hasColor) {
            let base = NODE_COLOR_CLASS[n.color!];
            if (completed && isSelected) {
              // 완성 시: 선과 같은 색의 외곽선을 노드 둘레에 함께 표시
              base += ' ring-2 ring-primary-500';
            }
            cls = `${base} pointer-events-none`;
          } else if (completed) {
            cls =
              'bg-primary-500 text-white pointer-events-none ring-2 ring-primary-500 ring-offset-2 ring-offset-background-50';
          } else if (isActive) {
            cls = 'bg-primary-600 text-white pointer-events-none';
          } else if (isSelected) {
            cls = 'bg-primary-500 text-white pointer-events-none';
          } else if (isWrong) {
            cls = 'bg-white border-2 border-rose-400 text-rose-500 pointer-events-none';
          } else {
            cls = 'bg-white border-2 border-secondary-300 text-foreground-800 pointer-events-none';
          }

          // 중심 정렬은 left/top 에서 직접 처리한다. transform 은 확대 연출 전용.
          const style: CSSProperties = {
            width: nodeSize,
            height: nodeSize,
            left: n.x * scale - nodeSize / 2,
            top: n.y * scale - nodeSize / 2,
            ...shapeStyle(n.shape),
          };
          if (isHover && !isWrong && !completed) style.transform = 'scale(1.15)';
          if (n.shape === 'triangle') style.paddingTop = 10;

          return (
            <div
              key={n.id}
              className={`absolute flex items-center justify-center font-bold select-none text-xl transition-transform duration-100 ${cls} ${
                isWrong || isEmptyWrong ? 'animate-node-shake' : ''
              } ${isActive && hasColor ? 'animate-step-pulse' : ''}`}
              style={style}
            >
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
}